import { Avatar, AvatarFallback, AvatarImage } from '@ophelos/ui/components/avatar';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card-layer';
import { Input } from '@ophelos/ui/components/input';
import { Label } from '@ophelos/ui/components/label';
import { Skeleton } from '@ophelos/ui/components/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { useAuth } from '@ophelos/ui/contexts/workos-auth-context';
import { getAvatarFallbackClass } from '@ophelos/ui/lib/avatar-colors';
import { cn } from '@ophelos/ui/lib/utils';
import { Check, LoaderCircle, Save, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import type { UserProfile } from '@/hooks/useUserProfile';
import { useUserProfile } from '@/hooks/useUserProfile';
import { getDisplayName, getInitials } from '@/lib/user-helpers';
import { AvatarSection } from './_partials/profile/AvatarSection';

function CopyValue({
  value,
  label,
  icon: Icon,
  fontClass,
}: {
  value: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  fontClass?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'inline-flex items-center gap-2 max-w-full truncate text-muted-foreground bg-muted hover:bg-muted/50 px-1.5 py-0.75 rounded-sm transition-colors cursor-pointer',

            fontClass === 'font-mono' ? 'font-mono text-xs' : 'text-sm'
          )}
        >
          {Icon && <Icon className="size-3 text-muted-foreground" />}
          <span className="truncate">{value}</span>
          {copied ? <Check className="size-2.5 text-green-600" /> : null}
        </button>
      </TooltipTrigger>
      <TooltipContent>{copied ? 'Copied!' : `Copy ${label}`}</TooltipContent>
    </Tooltip>
  );
}

export function ProfilePage() {
  const { user } = useAuth();
  const { profile, isLoading, isSaving, updateProfile } = useUserProfile();

  // Local draft state
  const [draftFirstName, setDraftFirstName] = useState('');
  const [draftLastName, setDraftLastName] = useState('');
  const [draftAvatarUrl, setDraftAvatarUrl] = useState<string | null>(null);
  const [draftAvatarColor, setDraftAvatarColor] = useState<string | null>(null);
  // Initialize draft from loaded profile (once) — setState during render avoids effect lint
  const [prevProfile, setPrevProfile] = useState<UserProfile | null>(null);
  if (profile && !prevProfile) {
    setPrevProfile(profile);
    setDraftFirstName(profile.first_name ?? user?.firstName ?? '');
    setDraftLastName(profile.last_name ?? user?.lastName ?? '');
    setDraftAvatarUrl(profile.avatar_url);
    setDraftAvatarColor(profile.avatar_color);
  }

  const initialized = !!prevProfile;

  const hasChanges =
    initialized &&
    (draftFirstName !== (profile?.first_name ?? user?.firstName ?? '') ||
      draftLastName !== (profile?.last_name ?? user?.lastName ?? '') ||
      draftAvatarUrl !== profile?.avatar_url ||
      draftAvatarColor !== profile?.avatar_color);

  const handleSave = async () => {
    try {
      await updateProfile({
        first_name: draftFirstName || null,
        last_name: draftLastName || null,
        avatar_url: draftAvatarUrl,
        avatar_color: draftAvatarColor,
      });
      toast.success('Profile saved');
    } catch {
      toast.error('Failed to save profile');
    }
  };

  const handleReset = () => {
    if (!profile) return;
    setDraftFirstName(profile.first_name ?? user?.firstName ?? '');
    setDraftLastName(profile.last_name ?? user?.lastName ?? '');
    setDraftAvatarUrl(profile.avatar_url);
    setDraftAvatarColor(profile.avatar_color);
  };

  const userName = draftFirstName || draftLastName ? [draftFirstName, draftLastName].filter(Boolean).join(' ') : getDisplayName(user!);

  // Avatar display logic for the card preview
  const avatarSrc = draftAvatarUrl ?? null;
  const avatarFallbackClass = getAvatarFallbackClass(draftAvatarColor);

  if (!user) return null;

  return (
    <div className=" w-full stack gap-10 h-full overflow-y-auto slim-scrollbar">
      <div className=" w-full max-w-4xl mx-auto stack gap-6 px-6 py-6 mt-[3vh]">
        <div className="flex justify-between items-start w-full flex-wrap gap-4">
          <div className="flex-1 stack gap-8">
            <div className="flex items-start gap-5">
              <AvatarSection
                currentAvatarUrl={draftAvatarUrl}
                currentAvatarColor={draftAvatarColor}
                userName={userName}
                onChange={(url, color) => {
                  setDraftAvatarUrl(url);
                  setDraftAvatarColor(color);
                }}
                renderTrigger={(onOpen) => (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={onOpen}
                        className="group/avatar relative rounded-full cursor-pointer focus-visible:outline-ring"
                      >
                        <Avatar key={`${draftAvatarUrl}-${draftAvatarColor}`} className="size-20 shrink-0">
                          {avatarSrc && <AvatarImage src={avatarSrc} alt={userName} />}
                          <AvatarFallback className={cn('font-semibold text-3xl', avatarFallbackClass)}>
                            {getInitials(userName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="absolute inset-0 rounded-full bg-foreground/0 group-hover/avatar:bg-foreground/10 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Edit picture</TooltipContent>
                  </Tooltip>
                )}
              />
              <div className="stack gap-1">
                <p className="text-xl font-semibold tracking-wide">{userName}</p>

                <div className="flex gap-1">
                  <CopyValue value={user.id} label="User ID" fontClass="font-mono" />
                  <CopyValue value={user.email} label="Email" />
                </div>
              </div>
            </div>
          </div>
          {hasChanges && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                <Undo2 className="size-4" />
                Undo Changes
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? <LoaderCircle className="size-4 animate-spin mr-2" /> : <Save className="size-4 mr-2" />}
                Save changes
              </Button>
            </div>
          )}
        </div>

        <div className="w-full space-y-6 mt-4">
          {isLoading ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                <Skeleton className="h-16 w-16 rounded-full" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">User Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="stack gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="first-name">First name</Label>
                    <Input
                      id="first-name"
                      value={draftFirstName}
                      onChange={(e) => setDraftFirstName(e.target.value)}
                      placeholder="First name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last-name">Last name</Label>
                    <Input
                      id="last-name"
                      value={draftLastName}
                      onChange={(e) => setDraftLastName(e.target.value)}
                      placeholder="Last name"
                    />
                  </div>
                  <div className="space-y-2 ">
                    <div className="stack gap-1">
                      <Label>Email</Label>
                      <p className="text-xs text-muted-foreground">Managed by your identity provider.</p>
                    </div>
                    <Input id="email" value={user.email} disabled className="" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
